import { useCallback, useEffect, useState } from 'react';
import styles from './Payouts.module.css';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Button from '../../components/Button/Button';
import Modal, { ModalTitle, ModalDivider, DetailRow } from '../../components/Modal/Modal';
import FilterSelect from '../../components/FilterSelect/FilterSelect';
import { api, commissionLabel, mapMoneyOrder, money, shortDate } from '../../lib/api';

// Money AgroBridge is holding from in-app checkout. Buyers pay us when they
// check out; we don't disburse by ourselves. When an order completes, the
// seller's payout (the full total — commission is taken in points) is HELD for a few days for the buyer
// to confirm or report a problem, then shows up here as a payout due. A
// dispute waits here for an admin to release it or refund the buyer, and a
// cancelled paid order's money shows up as a refund due. Send money by MoMo
// first, then record the transaction id — the same receipt model as affiliate
// payouts. The rules live in the API (utils/payouts.js, routes/admin.js).

const VIEWS = {
    'Seller payouts': {
        path: '/admin/order-payouts',
        statuses: { Due: 'due', Disputed: 'disputed', Held: 'held', 'Paid out': 'paid' },
    },
    'Buyer refunds': {
        path: '/admin/order-refunds',
        statuses: { Due: 'due', Refunded: 'refunded' },
    },
};

// The window the commission card reports on, as the API's from/to.
function periodRange(period) {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    switch (period) {
        case 'This month':
            return { from: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))), to: iso(now) };
        case 'Last month':
            return {
                from: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))),
                to: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0))),
            };
        case 'This year':
            return { from: iso(new Date(Date.UTC(now.getUTCFullYear(), 0, 1))), to: iso(now) };
        default:
            return { from: iso(new Date(now.getTime() - 30 * 864e5)), to: iso(now) };
    }
}

const PERIODS = ['Last 30 days', 'This month', 'Last month', 'This year'];

const Payouts = () => {
    const [view, setView] = useState('Seller payouts');
    const [statusLabel, setStatusLabel] = useState('Due');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({
        payoutsDue: null,
        payoutCount: 0,
        refundsDue: null,
        refundCount: 0,
        disputeCount: 0,
        heldTotal: 0,
    });
    const [period, setPeriod] = useState(PERIODS[0]);
    const [commission, setCommission] = useState(null);
    const [recordFor, setRecordFor] = useState(null);
    const [resolveFor, setResolveFor] = useState(null);
    const [resolveNote, setResolveNote] = useState('');
    const [form, setForm] = useState({ reference: '', momoNumber: '', note: '' });
    const [busy, setBusy] = useState(false);

    const isPayouts = view === 'Seller payouts';
    const { path, statuses } = VIEWS[view];
    const mode = statuses[statusLabel];
    const showingDue = mode === 'due';
    const showingDone = mode === 'paid' || mode === 'refunded';

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api(`${path}?status=${statuses[statusLabel]}`);
            setRows(data.orders.map(mapMoneyOrder));
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [path, statuses, statusLabel]);

    // What's owed on both sides, whichever list is open — so a refund waiting
    // isn't missed while someone works through payouts.
    const loadSummary = useCallback(async () => {
        try {
            const [payouts, refunds, disputed, held] = await Promise.all([
                api('/admin/order-payouts?status=due'),
                api('/admin/order-refunds?status=due'),
                api('/admin/order-payouts?status=disputed'),
                api('/admin/order-payouts?status=held'),
            ]);
            setSummary({
                payoutsDue: payouts.totalDue || 0,
                payoutCount: payouts.orders.length,
                refundsDue: refunds.orders.reduce((sum, o) => sum + (o.refund?.amount ?? o.total), 0),
                refundCount: refunds.orders.length,
                disputeCount: disputed.orders.length,
                heldTotal: held.total || 0,
            });
        } catch {
            // leave the last figures up
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    useEffect(() => {
        const { from, to } = periodRange(period);
        api(`/admin/commission?from=${from}&to=${to}`)
            .then(setCommission)
            .catch(() => setCommission(null));
    }, [period]);

    const openRecord = (row) => {
        setForm({
            reference: '',
            // Payouts go to the seller's account phone, refunds back to the
            // wallet that paid — both editable in case it has to go elsewhere.
            momoNumber: isPayouts ? row.sellerPhone : row.paidFrom || row.buyerPhone,
            note: '',
        });
        setRecordFor(row);
    };

    const record = async () => {
        setBusy(true);
        try {
            const body = { reference: form.reference.trim(), momoNumber: form.momoNumber.trim() };
            if (isPayouts && form.note.trim()) body.note = form.note.trim();
            await api(`/admin/orders/${recordFor._id}/${isPayouts ? 'payout' : 'refund'}`, { method: 'POST', body });
            setRecordFor(null);
            await Promise.all([load(), loadSummary()]);
        } catch (err) {
            alert(err.message);
        } finally {
            setBusy(false);
        }
    };

    const openResolve = (row) => {
        setResolveNote('');
        setResolveFor(row);
    };

    const resolve = async (action) => {
        setBusy(true);
        try {
            await api(`/admin/orders/${resolveFor._id}/dispute`, {
                method: 'POST',
                body: { action, note: resolveNote.trim() },
            });
            setResolveFor(null);
            await Promise.all([load(), loadSummary()]);
        } catch (err) {
            alert(err.message);
        } finally {
            setBusy(false);
        }
    };

    const dateHeader = {
        due: isPayouts ? 'Released' : 'Cancelled',
        held: 'Releases on',
        disputed: 'Reported',
        paid: 'Sent',
        refunded: 'Sent',
    }[mode];

    const emptyText = {
        due: `Nothing owed to ${isPayouts ? 'sellers' : 'buyers'} right now.`,
        held: 'No payouts waiting on buyers.',
        disputed: 'No open disputes.',
        paid: 'No payouts recorded yet.',
        refunded: 'No refunds recorded yet.',
    }[mode];

    const amountOf = (row) => (isPayouts ? row.payout.amount : row.refund.amount ?? row.total);

    return (
        <>
            <div className="row g-3 mb-4">
                <div className="col-lg-4 col-md-6">
                    <div className={`${styles.card} p-4 h-100`}>
                        <div className={styles.statLabel}>Owed to sellers</div>
                        <div className={styles.statValue}>
                            {summary.payoutsDue == null ? '--' : money(summary.payoutsDue)}
                        </div>
                        <div className={styles.sub}>
                            {summary.payoutCount} order{summary.payoutCount === 1 ? '' : 's'} ready to pay out
                            {summary.heldTotal > 0 && ` · ${money(summary.heldTotal)} still held for buyers`}
                        </div>
                        {summary.disputeCount > 0 && (
                            <div className={`${styles.sub} ${styles.alert} mt-1`}>
                                {summary.disputeCount} dispute{summary.disputeCount === 1 ? '' : 's'} to resolve
                            </div>
                        )}
                    </div>
                </div>
                <div className="col-lg-4 col-md-6">
                    <div className={`${styles.card} p-4 h-100`}>
                        <div className={styles.statLabel}>Owed to buyers</div>
                        <div className={`${styles.statValue} ${summary.refundCount ? styles.alert : ''}`}>
                            {summary.refundsDue == null ? '--' : money(summary.refundsDue)}
                        </div>
                        <div className={styles.sub}>
                            {summary.refundCount} order{summary.refundCount === 1 ? '' : 's'} waiting for a refund
                        </div>
                    </div>
                </div>
                <div className="col-lg-4 col-md-12">
                    <div className={`${styles.card} p-4 h-100`}>
                        <div className="d-flex align-items-start justify-content-between gap-2">
                            <div className={styles.statLabel}>Commission earned</div>
                            <FilterSelect label="Period" value={period} options={PERIODS} onChange={setPeriod} />
                        </div>
                        <div className={styles.statValue}>
                            {commission
                                ? `${(commission.paidInApp.commissionPoints || 0) + commission.offPlatform.commissionPoints} pts`
                                : '--'}
                        </div>
                        {commission && (
                            <>
                                <div className={styles.sub}>
                                    {commission.paidInApp.commissionPoints || 0} pts from {commission.paidInApp.orders} paid order
                                    {commission.paidInApp.orders === 1 ? '' : 's'} ({money(commission.paidInApp.sales)} sales)
                                </div>
                                {commission.offPlatform.orders > 0 && (
                                    <div className={styles.sub}>
                                        + {commission.offPlatform.commissionPoints} pts from{' '}
                                        {commission.offPlatform.orders} order{commission.offPlatform.orders === 1 ? '' : 's'} paid off the platform
                                    </div>
                                )}
                                {commission.paidInApp.commission > 0 && (
                                    <div className={styles.sub}>
                                        + {money(commission.paidInApp.commission)} kept from older payouts
                                        {commission.paidInApp.pending > 0 &&
                                            ` (${money(commission.paidInApp.pending)} not yet paid out)`}
                                    </div>
                                )}
                                <div className={`${styles.sub} mt-1`}>
                                    {(commission.rate * 100).toFixed(2).replace(/\.?0+$/, '')}% per order
                                    {commission.maxPoints > 0 && `, max ${commission.maxPoints} pts`} · payouts held{' '}
                                    {commission.holdDays} day{commission.holdDays === 1 ? '' : 's'}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className={`${styles.card} p-4`}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <h2 className={`${styles.cardTitle} mb-0`}>{view}</h2>
                    <div className="d-flex align-items-center flex-wrap gap-3">
                        <FilterSelect
                            label="Show"
                            value={view}
                            options={Object.keys(VIEWS)}
                            onChange={(value) => {
                                setView(value);
                                setStatusLabel('Due');
                            }}
                        />
                        <FilterSelect
                            label="Status"
                            value={statusLabel}
                            options={Object.keys(statuses)}
                            onChange={setStatusLabel}
                        />
                    </div>
                </div>

                <div className={`${styles.tableWrap} table-responsive mt-3`}>
                    <table className={`${styles.table} w-100`}>
                        <thead>
                            <tr>
                                <th>Order</th>
                                <th>{isPayouts ? 'Seller' : 'Buyer'}</th>
                                <th>Send to</th>
                                <th>Order total</th>
                                <th>{isPayouts ? 'Commission' : 'Paid with'}</th>
                                <th>{isPayouts ? 'Payout' : 'Refund'}</th>
                                <th>{dateHeader}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={8} className={styles.sub}>
                                        {emptyText}
                                    </td>
                                </tr>
                            )}
                            {rows.map((row) => {
                                const sent = isPayouts ? row.payout : row.refund;
                                return (
                                    <tr key={row._id}>
                                        <td>
                                            <div className={styles.name}>{row.code}</div>
                                            <div className={styles.sub}>{row.paymentRef}</div>
                                        </td>
                                        <td>
                                            <div className={styles.name}>{isPayouts ? row.seller : row.buyer}</div>
                                            {!isPayouts && row.cancelReason && (
                                                <div className={styles.sub}>“{row.cancelReason}”</div>
                                            )}
                                            {isPayouts && mode === 'disputed' && (
                                                <div className={styles.sub}>
                                                    {row.buyer}: “{row.dispute.reason || 'No reason given'}”
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {showingDone
                                                ? sent.momoNumber || '--'
                                                : isPayouts
                                                  ? row.sellerPhone
                                                  : row.paidFrom || row.buyerPhone}
                                        </td>
                                        <td>{money(row.total)}</td>
                                        <td>{isPayouts ? commissionLabel(row) : row.provider || '--'}</td>
                                        <td className={styles.due}>{money(amountOf(row))}</td>
                                        <td>
                                            {showingDone ? (
                                                <>
                                                    <div>{shortDate(isPayouts ? sent.paidAt : sent.refundedAt)}</div>
                                                    <div className={styles.sub}>ref {sent.reference}</div>
                                                </>
                                            ) : mode === 'held' ? (
                                                shortDate(row.payout.releaseAt)
                                            ) : mode === 'disputed' ? (
                                                shortDate(row.dispute.openedAt)
                                            ) : isPayouts ? (
                                                <>
                                                    <div>{shortDate(row.payout.dueAt || row.completedAt)}</div>
                                                    {row.receivedConfirmedAt && (
                                                        <div className={styles.sub}>buyer confirmed</div>
                                                    )}
                                                </>
                                            ) : (
                                                shortDate(row.cancelledAt)
                                            )}
                                        </td>
                                        <td className="text-end">
                                            {showingDue && (
                                                <Button variant="outline" onClick={() => openRecord(row)}>
                                                    {isPayouts ? 'Record payout' : 'Record refund'}
                                                </Button>
                                            )}
                                            {mode === 'disputed' && (
                                                <Button variant="outline" onClick={() => openResolve(row)}>
                                                    Resolve
                                                </Button>
                                            )}
                                            {mode === 'held' && <StatusBadge status="Waiting on buyer" variant="Pending" />}
                                            {showingDone && (
                                                <StatusBadge status={isPayouts ? 'Paid out' : 'Refunded'} variant="Active" />
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {recordFor && (
                <Modal onClose={() => setRecordFor(null)} centered>
                    <ModalTitle>{isPayouts ? 'Record payout' : 'Record refund'}</ModalTitle>
                    <div className={`${styles.amount} mt-3`}>{money(amountOf(recordFor))}</div>
                    <p className={styles.helpText}>
                        {isPayouts
                            ? `Send this by MoMo to ${recordFor.seller} first, then enter the transaction reference. ` +
                              `${recordFor.seller} is told it has been sent.`
                            : `Send this by MoMo back to ${recordFor.buyer} first, then enter the transaction reference. ` +
                              `${recordFor.buyer} is told it has been sent.`}
                    </p>

                    <ModalDivider />

                    <DetailRow label="Order" className="mb-3">
                        <span>{recordFor.code}</span>
                    </DetailRow>
                    <DetailRow label="Order total" className="mb-3">
                        <span>{money(recordFor.total)}</span>
                    </DetailRow>
                    {isPayouts && (
                        <DetailRow label="Commission" className="mb-3">
                            <span>{commissionLabel(recordFor)}</span>
                        </DetailRow>
                    )}
                    <DetailRow label="Buyer paid with">
                        <span>
                            {recordFor.provider || '--'}
                            {recordFor.providerTxId ? ` · ${recordFor.providerTxId}` : ''}
                        </span>
                    </DetailRow>

                    <label htmlFor="pay-number" className={`${styles.inputLabel} mt-4 mb-2`}>
                        Sent to
                    </label>
                    <input
                        id="pay-number"
                        className={`${styles.input} w-100`}
                        value={form.momoNumber}
                        onChange={(event) => setForm({ ...form, momoNumber: event.target.value })}
                    />

                    <label htmlFor="pay-ref" className={`${styles.inputLabel} mt-3 mb-2`}>
                        MoMo transaction reference
                    </label>
                    <input
                        id="pay-ref"
                        className={`${styles.input} w-100`}
                        value={form.reference}
                        autoFocus
                        onChange={(event) => setForm({ ...form, reference: event.target.value })}
                    />

                    {isPayouts && (
                        <>
                            <label htmlFor="pay-note" className={`${styles.inputLabel} mt-3 mb-2`}>
                                Note (optional)
                            </label>
                            <input
                                id="pay-note"
                                className={`${styles.input} w-100`}
                                value={form.note}
                                onChange={(event) => setForm({ ...form, note: event.target.value })}
                            />
                        </>
                    )}

                    <Button
                        className="w-100 mt-4"
                        disabled={busy || !form.reference.trim() || !form.momoNumber.trim()}
                        onClick={record}
                    >
                        {isPayouts ? 'Mark as paid' : 'Mark as refunded'}
                    </Button>
                </Modal>
            )}
            {resolveFor && (
                <Modal onClose={() => setResolveFor(null)} centered>
                    <ModalTitle>Resolve dispute</ModalTitle>
                    <p className={`${styles.helpText} mt-2`}>
                        {resolveFor.buyer} reported a problem with order {resolveFor.code} from {resolveFor.seller}. Check
                        with both of them (the buyer is asked to explain in Support), then decide.
                    </p>

                    <ModalDivider />

                    <DetailRow label="Buyer says" className="mb-3">
                        <span>“{resolveFor.dispute.reason || 'No reason given'}”</span>
                    </DetailRow>
                    <DetailRow label="Reported" className="mb-3">
                        <span>{shortDate(resolveFor.dispute.openedAt)}</span>
                    </DetailRow>
                    <DetailRow label="Order total" className="mb-3">
                        <span>{money(resolveFor.total)}</span>
                    </DetailRow>
                    <DetailRow label="Seller would get">
                        <span>{money(resolveFor.payout.amount)}</span>
                    </DetailRow>

                    <label htmlFor="resolve-note" className={`${styles.inputLabel} mt-4 mb-2`}>
                        Note (optional, for the record)
                    </label>
                    <input
                        id="resolve-note"
                        className={`${styles.input} w-100`}
                        value={resolveNote}
                        onChange={(event) => setResolveNote(event.target.value)}
                    />

                    <div className="d-flex gap-3 mt-4">
                        <Button className="w-100" disabled={busy} onClick={() => resolve('release')}>
                            Pay the seller
                        </Button>
                        <Button className="w-100" variant="outline" disabled={busy} onClick={() => resolve('refund')}>
                            Refund the buyer
                        </Button>
                    </div>
                    <p className={`${styles.helpText} mt-3 mb-0`}>
                        Paying the seller moves it to Due. Refunding moves it to Buyer refunds, where you record the MoMo
                        transfer back.
                    </p>
                </Modal>
            )}
        </>
    );
};

export default Payouts;
